import { useMemo, useState } from 'react';
import { LogOut, RefreshCw, Wifi, WifiOff, Bell } from 'lucide-react';
import { useAuth } from '../providers/AuthProvider';
import { useQueueContext } from '../providers/QueueProvider';
import { useQueueQuery, useCallNext } from '../hooks/useQueueQuery';
import { useWebSocketStore } from '../stores/websocketStore';
import { OrderCard } from '../components/OrderCard';
import { CurrentOrder } from '../components/CurrentOrder';
import type { Shop, Order } from '../types';

interface QueuePageProps {
  shop: Shop;
}

export function QueuePage({ shop }: QueuePageProps) {
  const { logout } = useAuth();
  const { shopId } = useQueueContext();
  const { isConnected } = useWebSocketStore();

  // Get queue data from TanStack Query
  const { data, isLoading, refetch, dataUpdatedAt } = useQueueQuery(shopId);
  const orders = data?.orders ?? [];

  // Debug: Log when query data changes
  console.log('[QueuePage] Render - dataUpdatedAt:', dataUpdatedAt, 'orders:', orders.map(o => ({ id: o.id.slice(0, 8), status: o.status })));

  // Mutations
  const callNextMutation = useCallNext(shopId);

  // Local error state for displaying errors
  const [error, setError] = useState<string | null>(null);

  // Handle call next
  const handleCallNext = async () => {
    try {
      setError(null);
      await callNextMutation.mutateAsync();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل استدعاء العميل التالي');
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Categorize orders
  const { pendingOrders, calledOrder, activeOrders, readyOrders } = useMemo(() => {
    const pending: Order[] = [];
    const active: Order[] = [];
    const ready: Order[] = [];
    let called: Order | null = null;

    orders.forEach((order) => {
      switch (order.status) {
        case 'pending':
          pending.push(order);
          break;
        case 'called':
          called = order;
          break;
        case 'confirmed':
        case 'preparing':
          active.push(order);
          break;
        case 'ready':
          ready.push(order);
          break;
      }
    });

    // Sort pending by queue number
    pending.sort((a, b) => a.queueNumber - b.queueNumber);
    // Sort active by time (oldest first)
    active.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return {
      pendingOrders: pending,
      calledOrder: called,
      activeOrders: active,
      readyOrders: ready,
    };
  }, [orders]);

  // Show loading state
  if (isLoading && orders.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          <p className="text-gray-400">جاري تحميل الطابور...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-surface">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-border bg-surface-light px-4 py-3">
        <div className="flex items-center gap-3">
          {shop.logoUrl ? (
            <img src={shop.logoUrl} alt={shop.nameAr} className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/20">
              <span className="text-lg font-bold text-primary-500">
                {shop.nameAr.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <h1 className="font-bold text-white">{shop.nameAr}</h1>
            <div className="flex items-center gap-2 text-xs">
              {isConnected ? (
                <span className="flex items-center gap-1 text-green-400">
                  <Wifi className="h-3 w-3" />
                  متصل
                </span>
              ) : (
                <span className="flex items-center gap-1 text-amber-400">
                  <WifiOff className="h-3 w-3" />
                  غير متصل
                </span>
              )}
              <span className="text-gray-500">•</span>
              <span className="text-gray-400">
                {pendingOrders.length} في الانتظار
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-surface hover:text-white"
            title="تحديث"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button
            onClick={() => logout()}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-surface hover:text-red-400"
            title="تسجيل الخروج"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 overflow-hidden">
        {/* Left Panel - Queue & Waiting */}
        <div className="flex w-64 md:w-72 lg:w-80 xl:w-96 flex-col border-l border-border">
          {/* Call Next Button */}
          <div className="border-b border-border p-4">
            <button
              onClick={handleCallNext}
              disabled={pendingOrders.length === 0 || calledOrder !== null || callNextMutation.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-4 text-lg font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {callNextMutation.isPending ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Bell className="h-6 w-6" />
              )}
              <span>استدعاء التالي</span>
            </button>
            {calledOrder && (
              <p className="mt-2 text-center text-sm text-amber-400">
                يوجد عميل قيد الانتظار للتأكيد
              </p>
            )}
          </div>

          {/* Pending Queue */}
          <div className="flex-1 overflow-y-auto p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-400">
              قائمة الانتظار ({pendingOrders.length})
            </h2>
            {pendingOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-gray-500">لا يوجد طلبات في الانتظار</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingOrders.map((order, index) => (
                  <div
                    key={order.id}
                    className={`rounded-xl border p-3 ${
                      index === 0
                        ? 'border-primary-500/50 bg-primary-500/10'
                        : 'border-border bg-surface-light'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-white">
                        #{order.queueNumber}
                      </span>
                      <span className="text-sm text-gray-400">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} منتج
                      </span>
                    </div>
                    {order.customerName && (
                      <p className="mt-1 text-sm text-gray-400">{order.customerName}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Center Panel - Current Order */}
        <div className="flex flex-1 flex-col">
          <CurrentOrder order={calledOrder} shopId={shopId} />
        </div>

        {/* Right Panel - Active & Ready Orders */}
        <div className="flex w-64 md:w-72 lg:w-80 xl:w-96 flex-col border-r border-border">
          {/* Active Orders */}
          <div className="flex-1 overflow-y-auto border-b border-border p-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-400">
              قيد التحضير ({activeOrders.length})
            </h2>
            {activeOrders.length === 0 ? (
              <p className="text-center text-sm text-gray-500">لا يوجد طلبات قيد التحضير</p>
            ) : (
              <div className="space-y-3">
                {activeOrders.map((order) => (
                  <OrderCard key={order.id} order={order} variant="compact" shopId={shopId} />
                ))}
              </div>
            )}
          </div>

          {/* Ready Orders */}
          <div className="flex-1 overflow-y-auto p-4">
            <h2 className="mb-3 text-sm font-semibold text-green-400">
              جاهز للاستلام ({readyOrders.length})
            </h2>
            {readyOrders.length === 0 ? (
              <p className="text-center text-sm text-gray-500">لا يوجد طلبات جاهزة</p>
            ) : (
              <div className="space-y-3">
                {readyOrders.map((order) => (
                  <OrderCard key={order.id} order={order} variant="compact" shopId={shopId} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-xl bg-red-500/90 px-4 py-2 text-white shadow-lg">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-white/80 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
